import { createImmerReducer } from "utils/ReducerUtils";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import type { ReduxAction } from "actions/ReduxActionTypes";
import type { Module } from "ee/constants/ModuleConstants";
import type {
  FetchPackageResponse,
  FetchConsumablePackagesInWorkspaceResponse,
  PullPackageResponse,
} from "ee/api/PackageApi";
import type {
  CreateUIModuleSuccessPayload,
  DeleteModulePayload,
  UpdateModuleDSLPayload,
  UpdateModuleInputsPayload,
  UpdateModuleOutputsPayload,
} from "ee/actions/moduleActions";
import { klona } from "klona";
import type { ConvertEntityToInstanceResponse } from "ee/api/ModuleInstanceApi";
import type { CreateModuleResponse } from "ee/api/ModuleApi";
import type { ApiResponse } from "api/ApiResponses";
import { omit } from "lodash";

type ID = string;

export type ModulesReducerState = Record<ID, Module>;

export const initialState: ModulesReducerState = {};

const updateModule = (
  draftState: ModulesReducerState,
  action: ReduxAction<Module>,
) => {
  if (!action.payload) return;

  const { id } = action.payload;

  draftState[id] = action.payload;
};

const modulesReducer = createImmerReducer(initialState, {
  [ReduxActionTypes.FETCH_PACKAGE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<FetchPackageResponse>,
  ) => {
    draftState = klona(initialState);

    const { modules } = action.payload;

    modules.forEach((module: Module) => {
      draftState[module.id] = module;
    });

    return draftState;
  },

  [ReduxActionTypes.SAVE_MODULE_NAME_SUCCESS]: updateModule,
  [ReduxActionTypes.DELETE_MODULE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<DeleteModulePayload>,
  ) => {
    const { id: moduleId } = action.payload;

    delete draftState[moduleId];
  },
  [ReduxActionTypes.CREATE_QUERY_MODULE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<CreateModuleResponse>,
  ) => {
    draftState[action.payload.id] = omit(action.payload, "metadata");
  },
  [ReduxActionTypes.CREATE_JS_MODULE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<CreateModuleResponse>,
  ) => {
    draftState[action.payload.id] = omit(action.payload, "metadata");
  },
  [ReduxActionTypes.UPDATE_MODULE_INPUTS_INIT]: (
    draftState: ModulesReducerState,
    action: ReduxAction<UpdateModuleInputsPayload>,
  ) => {
    const { id, inputsForm } = action.payload;

    draftState[id].inputsForm = inputsForm;

    return draftState;
  },
  [ReduxActionTypes.UPDATE_MODULE_OUTPUTS_INIT]: (
    draftState: ModulesReducerState,
    action: ReduxAction<UpdateModuleOutputsPayload>,
  ) => {
    const { id, outputsForm } = action.payload;

    draftState[id].outputsForm = outputsForm;

    return draftState;
  },
  [ReduxActionTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<FetchConsumablePackagesInWorkspaceResponse>,
  ) => {
    draftState = klona(initialState);
    const { modules } = action.payload;

    modules.forEach((module: Module) => {
      draftState[module.id] = module;
    });

    return draftState;
  },

  [ReduxActionTypes.PULL_PACKAGE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<ApiResponse<PullPackageResponse>>,
  ) => {
    draftState = klona(initialState);
    const { modules = [] } = action.payload.data.consumables || {};

    modules.forEach((module: Module) => {
      draftState[module.id] = module;
    });

    return draftState;
  },
  [ReduxActionTypes.RESET_EDITOR_REQUEST]: () => {
    return klona(initialState);
  },

  [ReduxActionTypes.UPDATE_MODULE_INPUTS_SUCCESS]: updateModule,

  [ReduxActionTypes.UPDATE_MODULE_ON_INPUT_CHANGE_SUCCESS]: updateModule,

  [ReduxActionTypes.UPDATE_UI_MODULE_ON_INPUT_CHANGE_DYNAMIC_PROPERTY_SUCCESS]:
    updateModule,

  [ReduxActionTypes.CONVERT_ENTITY_TO_INSTANCE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<ConvertEntityToInstanceResponse>,
  ) => {
    const { module } = action.payload;

    draftState[module.id] = module;
  },

  [ReduxActionTypes.UPDATE_MODULE_SUCCESS]: updateModule,

  [ReduxActionTypes.CREATE_UI_MODULE_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<CreateUIModuleSuccessPayload>,
  ) => {
    const { module } = action.payload;

    draftState[module.id] = module;

    return draftState;
  },

  [ReduxActionTypes.UPDATE_MODULE_LAYOUT_SUCCESS]: (
    draftState: ModulesReducerState,
    action: ReduxAction<{ module: Module }>,
  ) => {
    const { module } = action.payload;

    draftState[module.id] = module;
  },

  [ReduxActionTypes.UPDATE_MODULE_DSL]: (
    draftState: ModulesReducerState,
    action: ReduxAction<UpdateModuleDSLPayload>,
  ) => {
    const { dsl, moduleId } = action.payload;

    if (
      moduleId in draftState &&
      draftState[moduleId]?.layouts?.[0] &&
      typeof draftState[moduleId].layouts[0] === "object" &&
      "dsl" in draftState[moduleId].layouts[0]
    ) {
      draftState[moduleId].layouts[0].dsl = dsl;
    }

    return draftState;
  },
  [ReduxActionTypes.FETCH_MODULE_BY_ID_SUCCESS]: updateModule,
});

export default modulesReducer;
